/** SUNTRONIX ONE — Global KYC / Vendor Docs drawer.
 *
 *  A right-side sheet that opens from anywhere in the app via the
 *  header shortcut.  Provides fast-access sections (Most used,
 *  Company KYC, GST/Tax, Banking, MSME, Company Profile, OEM
 *  certificates, Finance docs), search, previews, downloads, and
 *  "Copy reference" quick actions — without forcing the user out of
 *  Sales Desk / Customer 360 / Quotation flows.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Search, X, Copy, Eye, Download, Loader2, ArrowUpRight,
  FileText, Sparkles, Building2,
} from "lucide-react";

const SECTIONS = [
  { key: "most_used",                title: "Most used" },
  { key: "company_kyc",         title: "Company KYC" },
  { key: "gst_tax",                     title: "GST / Tax" },
  { key: "banking",                    title: "Banking" },
  { key: "msme",                          title: "MSME / Udyam" },
  { key: "company_profile",   title: "Company profile" },
  { key: "oem_certificates", title: "OEM certificates" },
  { key: "finance_documents", title: "Finance documents" },
];

export function GlobalKycDrawerButton({ onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid="one-bar-kyc"
      title="KYC / Vendor Docs"
      aria-label="KYC / Vendor Docs"
      className="h-9 px-2 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] transition-colors inline-flex items-center gap-1.5">
      <ShieldCheck className="h-4 w-4" strokeWidth={1.7} />
      <span className="hidden lg:inline text-[11px] font-medium">KYC</span>
    </button>
  );
}

export default function GlobalKycDrawer({ open, onClose, user }) {
  const [snapshot, setSnapshot] = useState(null);
  const [q, setQ] = useState("");
  const [searchRows, setSearchRows] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dataCard, setDataCard] = useState(null);

  const loadSnapshot = useCallback(async () => {
    setBusy(true);
    try {
      const [snap, card] = await Promise.all([
        api.get("/kyc/drawer/snapshot"),
        api.get("/kyc/data-card"),
      ]);
      setSnapshot(snap.data);
      setDataCard(card.data || {});
    } catch { toast.error("Failed to load KYC drawer"); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (open) loadSnapshot();
    else { setQ(""); setSearchRows(null); }
  }, [open, loadSnapshot]);

  useEffect(() => {
    if (!open || !q.trim()) { setSearchRows(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/kyc/documents",
            { params: { q: q.trim(), limit: 30 } });
        setSearchRows(data.items || []);
      } catch { setSearchRows([]); }
      finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const isInternal = user && !["customer_global_admin", "customer_it_admin",
                                                            "customer_finance"].includes(user.role);
  if (!open || !isInternal) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="kyc-drawer">
      <div className="fixed inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white h-full w-full sm:w-[560px] max-w-full shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-heading font-semibold text-slate-900">
              KYC / Vendor Docs
            </div>
            <div className="text-[10px] text-slate-500">
              Internal — Suntronix corporate documents
            </div>
          </div>
          <Link to="/administration/corporate-kyc" onClick={onClose}
              data-testid="kyc-drawer-open-vault"
              className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            Open vault <ArrowUpRight className="h-3 w-3" />
          </Link>
          <button onClick={onClose} data-testid="kyc-drawer-close"
              className="p-1 rounded hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="kyc-drawer-search"
                placeholder="Search: name, type, reference, category, entity"
                className="w-full pl-8 pr-2 py-1.5 text-[12px] border border-slate-200 rounded-md focus:outline-none focus:border-slate-400" />
          </div>
        </div>

        <div className="p-3 space-y-3">
          <DataCardQuickCopies card={dataCard || {}} />

          {q.trim() && (
            <SectionBlock title={`Results${searching ? " · searching…" : ""}`}
                                       items={searchRows} onReload={loadSnapshot} />
          )}
          {!q.trim() && !snapshot && busy && (
            <div className="text-center text-[11px] text-slate-400 py-6">
              <Loader2 className="h-4 w-4 inline animate-spin mr-1" />
              Loading corporate documents…
            </div>
          )}
          {!q.trim() && snapshot &&
            SECTIONS.map((s) => (
              <SectionBlock key={s.key} title={s.title}
                                          items={snapshot[s.key]} onReload={loadSnapshot} />
            ))}
        </div>
      </div>
    </div>
  );
}

function DataCardQuickCopies({ card }) {
  const chips = useMemo(() => [
    { k: "gstin",                             label: "GSTIN" },
    { k: "pan",                                    label: "PAN" },
    { k: "udyam_msme",              label: "MSME" },
    { k: "legal_business_name", label: "Legal name" },
    { k: "primary_email",             label: "Email" },
    { k: "primary_phone",             label: "Phone" },
  ], []);
  const visible = chips.filter((c) => card[c.k]);
  if (!visible.length) return null;
  return (
    <div className="border border-slate-200 rounded-md p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
        <Building2 className="h-3 w-3" /> Corporate data · one-tap copy
      </div>
      <div className="flex flex-wrap gap-1">
        {visible.map((c) => (
          <button key={c.k} onClick={() =>
                  navigator.clipboard.writeText(card[c.k] || "")
                      .then(() => toast.success(`${c.label} copied`))}
              className="text-[11px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
            <Copy className="h-3 w-3" /> <span className="font-mono">{card[c.k]}</span>
            <span className="text-slate-400"> · {c.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SectionBlock({ title, items, onReload }) {
  if (!items) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
        <Sparkles className="h-3 w-3" /> {title}
      </div>
      {items.length === 0 && (
        <div className="text-[11px] text-slate-400 pl-4">— nothing yet</div>
      )}
      <div className="space-y-1">
        {items.map((d) => <DocRow key={d.id} doc={d} onReload={onReload} />)}
      </div>
    </div>
  );
}

function DocRow({ doc, onReload }) {
  const stream = async (kind) => {
    try {
      const r = await api.get(`/kyc/documents/${doc.id}/${kind}`,
          { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (kind === "preview") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = doc.filename || "document"; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 6000);
      onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Not permitted"); }
  };
  return (
    <div className="flex items-center gap-2 border border-slate-100 rounded-md px-2 py-1.5 hover:bg-slate-50"
      data-testid={`kyc-drawer-row-${doc.id}`}>
      <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-medium text-slate-900 truncate">{doc.title}</div>
        <div className="text-[10px] text-slate-500 truncate">
          {(doc.doc_type || "").replace(/_/g, " ")} · v{doc.version}
          {doc.reference_no && <> · <span className="font-mono">{doc.reference_no}</span></>}
        </div>
      </div>
      <button onClick={() => stream("preview")} title="Preview"
          className="p-1 rounded hover:bg-white border border-slate-200">
        <Eye className="h-3 w-3" />
      </button>
      <button onClick={() => stream("download")} title="Download"
          className="p-1 rounded hover:bg-white border border-slate-200">
        <Download className="h-3 w-3" />
      </button>
      {doc.reference_no && (
        <button onClick={() =>
                navigator.clipboard.writeText(doc.reference_no)
                    .then(() => toast.success("Reference copied"))}
            title="Copy reference"
            className="p-1 rounded hover:bg-white border border-slate-200">
          <Copy className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
